import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Building2, DoorOpen, FileSignature, HardHat, Search, UserSquare2, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { useDebounced } from '@/hooks/useListParams';
import { Spinner } from '@/components/ui';

interface Hit {
  id: string;
  label: string;
  sublabel?: string;
  link: string;
}

type SearchResult = Partial<Record<'tenants' | 'tenancies' | 'properties' | 'units' | 'contractors' | 'owners', Hit[]>>;

const GROUPS: { key: keyof SearchResult; label: string; icon: typeof Users }[] = [
  { key: 'tenants', label: 'Mieter', icon: Users },
  { key: 'tenancies', label: 'Mietverhältnisse', icon: FileSignature },
  { key: 'properties', label: 'Immobilien', icon: Building2 },
  { key: 'units', label: 'Einheiten', icon: DoorOpen },
  { key: 'owners', label: 'Eigentümer', icon: UserSquare2 },
  { key: 'contractors', label: 'Handwerker', icon: HardHat },
];

/** Globale Suche (Strg+K): Mieter, Adresse, IBAN, Wohnung, Telefon, E-Mail, Vertragsnummer */
export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const debounced = useDebounced(q.trim(), 200);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<SearchResult>('/search', { q: debounced, limit: 6 }),
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  });
  const flat = useMemo(() => GROUPS.flatMap((g) => (data?.[g.key] ?? []).map((h) => ({ ...h, group: g }))), [data]);

  useEffect(() => {
    if (open) {
      setQ('');
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);
  useEffect(() => setActive(0), [debounced]);

  if (!open) return null;
  const go = (link: string) => {
    onClose();
    navigate(link);
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh]" role="dialog" aria-modal="true" aria-label="Globale Suche">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center gap-2 border-b border-slate-200 px-4">
          <Search className="h-5 w-5 text-slate-400" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, flat.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              }
              if (e.key === 'Enter' && flat[active]) go(flat[active].link);
            }}
            placeholder="Name, Adresse, IBAN, Telefon, E-Mail, Wohnung oder Vertragsnummer …"
            className="h-14 flex-1 border-0 bg-transparent text-base outline-none placeholder:text-slate-400"
          />
          {isFetching && <Spinner className="h-4 w-4 text-slate-400" />}
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {debounced.length < 2 && <p className="px-3 py-6 text-center text-sm text-slate-500">Mindestens 2 Zeichen eingeben.</p>}
          {debounced.length >= 2 && !isFetching && flat.length === 0 && <p className="px-3 py-6 text-center text-sm text-slate-500">Keine Treffer für „{debounced}“.</p>}
          {GROUPS.map((g) => {
            const hits = data?.[g.key] ?? [];
            if (!hits.length) return null;
            return (
              <div key={g.key} className="mb-2">
                <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{g.label}</p>
                {hits.map((h) => {
                  const idx = flat.findIndex((f) => f.link === h.link && f.id === h.id);
                  return (
                    <button
                      key={`${g.key}-${h.id}`}
                      type="button"
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => go(h.link)}
                      className={clsx('flex w-full items-center gap-3 rounded-md px-3 py-2 text-left', idx === active ? 'bg-brand-50' : 'hover:bg-slate-50')}
                    >
                      <g.icon className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-800">{h.label}</span>
                        {h.sublabel && <span className="block truncate text-xs text-slate-500">{h.sublabel}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
